import { useCurrentTheme } from '../../lib/theme';

/**
 * Logo
 *
 * Two modes:
 *   1. Default — renders the inline SVG mark + text "MagicIntake".
 *      The "Magic" word follows the theme via CSS variables, so it appears navy
 *      in light mode and white in dark mode automatically.
 *   2. Real logo — drop file(s) at:
 *        - public/brand-logo-light.svg  (used on light backgrounds / light theme)
 *        - public/brand-logo-dark.svg   (used on dark  backgrounds / dark  theme)
 *      Pass `useFile` to render via <img>. Falls back to /brand-logo.svg if the
 *      theme-specific file isn't found, then to the inline mock if neither exists.
 *
 *      The "MagicIntake" wordmark is rendered next to the image (just like the
 *      inline mark version) so the file logo only needs to be the icon/mark itself.
 *
 * Variants:
 *   - 'auto' (default) — text follows theme
 *   - 'light' — forces navy text (for use on light backgrounds when theme can't be trusted)
 *   - 'dark' — forces white text (for use on dark backgrounds, e.g. portal headers)
 *   - 'mono-white' — entire mark + text in white
 *   - 'mono-navy'  — entire mark + text in navy
 */
export default function Logo({ size = 32, variant = 'auto', showWord = true, useFile = false }) {
  const liveTheme = useCurrentTheme();

  // Compute the text colors used in BOTH file-mode and inline-mode.
  const isAuto = variant === 'auto';
  const magicColor =
    isAuto                                          ? 'var(--text)' :
    variant === 'dark' || variant === 'mono-white'  ? '#ffffff' :
    variant === 'mono-navy'                         ? '#0a0f2e' :
                                                      '#0a0f2e';
  const intakeColor =
    variant === 'mono-white' ? '#8be89e' :
    variant === 'mono-navy'  ? '#0a0f2e' :
                               '#3dc75b';

  if (useFile) {
    // Decide which file based on theme. 'auto' uses the live theme so toggles work.
    let theme;
    if (variant === 'dark' || variant === 'mono-white') theme = 'dark';
    else if (variant === 'light' || variant === 'mono-navy') theme = 'light';
    else theme = liveTheme;

    const file = theme === 'dark' ? '/brand-logo-dark.svg' : '/brand-logo-light.svg';

    return (
      <div style={{ display: 'inline-flex', alignItems: 'center', gap: 10 }}>
        <img
          // Force re-mount when theme flips so the browser actually re-fetches
          key={file}
          src={file}
          alt="MagicIntake"
          height={size}
          style={{ height: size, width: 'auto', display: 'block' }}
          // If theme-specific file missing, try single-file fallback, then hide.
          onError={(e) => {
            if (!e.currentTarget.dataset.fallback) {
              e.currentTarget.dataset.fallback = '1';
              e.currentTarget.src = '/brand-logo.svg';
            } else {
              e.currentTarget.style.display = 'none';
            }
          }}
        />
        {showWord && (
          <span style={{
            fontSize: size * 0.62,
            fontWeight: 700,
            letterSpacing: '-0.02em',
            color: magicColor,
            fontFamily: 'Inter, sans-serif',
          }}>
            Magic
            <span style={{ color: intakeColor }}>Intake</span>
          </span>
        )}
      </div>
    );
  }

  // ── Inline SVG mock ──

  // SVG mark colors
  const navy =
    variant === 'dark' || variant === 'mono-white' ? '#ffffff' :
    variant === 'mono-navy' ? '#0a0f2e' :
    '#0a0f2e';
  const green =
    variant === 'mono-white' ? '#ffffff' :
    variant === 'mono-navy'  ? '#0a0f2e' :
    '#3dc75b';
  const greenLight =
    variant === 'mono-white' ? '#ffffff' :
    variant === 'mono-navy'  ? '#0a0f2e' :
    '#8be89e';
  const spark = '#ffffff';

  return (
    <div style={{ display: 'inline-flex', alignItems: 'center', gap: 8 }}>
      <svg width={size} height={size} viewBox="0 0 40 40" fill="none" xmlns="http://www.w3.org/2000/svg">
        <path d="M22 4 C 28 3, 34 6, 37 12 L 30 14 L 28 20 L 20 18 Z" fill={green} opacity="0.95"/>
        <path d="M6 14 L 16 12 L 20 4 L 24 14 L 34 18 L 24 22 L 20 32 L 16 22 L 6 20 Z" fill={navy}/>
        <path d="M14 18 L 20 14 L 22 18 L 28 20 L 22 22 L 20 28 L 18 22 L 14 20 Z" fill={greenLight} opacity="0.75"/>
        <path d="M17 18.5 L 20 17 L 21.5 19 L 23.5 20 L 21.5 21 L 20 23 L 18.5 21 L 17 20 Z" fill={spark}/>
      </svg>
      {showWord && (
        <span style={{
          fontSize: size * 0.62,
          fontWeight: 700,
          letterSpacing: '-0.02em',
          color: magicColor,
          fontFamily: 'Inter, sans-serif',
        }}>
          Magic
          <span style={{ color: intakeColor }}>Intake</span>
        </span>
      )}
    </div>
  );
}
